export const createViewerMappingPanelController = ({
  state,
  mappingPanel,
  cablingPlanPanel,
  viewerSettingsAccordionButtons,
  viewerSettingsAccordionPanels,
  tabButtons,
  tabPanels,
  placementTabButtons,
  placementTabPanels,
  panelModeButtons,
  advancedPanelModeButtons,
  panelModePanels,
  guidedStepLabels,
  guidedEntry,
  guidedNav,
  guidedSharedStepLabel,
  guidedHeaderEl,
  advancedPanelModeSwitcher,
  editorModeLink,
  editorModeHintEl,
  guidedTitleEl,
  tagSelectionSection,
  tagThingSection,
  tagIntegrationSection,
  tagListSection,
  placeSelectionSection,
  placeThingSection,
  placeIntegrationSection,
  placeListSection,
  navigateSelectionSection,
  navigateListSection,
  advancedTagAccordionButtons,
  advancedTagAccordionPanels,
  advancedPlaceAccordionButtons,
  advancedPlaceAccordionPanels,
  advancedNavigateAccordionButtons,
  advancedNavigateAccordionPanels,
  advancedCaptureAccordionButtons,
  advancedCaptureAccordionPanels,
  guidedPrevButton,
  guidedStepNextButton,
  guidedFinishButton,
  renderReferenceLayerMenu,
  renderReferenceLayerList,
  syncCablingPlanForm,
  selectedPlacementPosition,
  selectedNodeKey,
  selectedMapping,
  clearHoveredObject,
  clearSelectedObject,
  renderInspector,
  setFeedback,
  renderer
}) => {
  const renderCablingPlanEditor = () => {
    if (mappingPanel) mappingPanel.hidden = state.sidePanelMode !== "mapping"
    if (cablingPlanPanel) cablingPlanPanel.hidden = state.sidePanelMode !== "viewer-settings"

    viewerSettingsAccordionButtons.forEach((button) => {
      const expanded = state.viewerSettingsSection === button.dataset.viewerSettingsAccordion
      button.classList.toggle("is-expanded", expanded)
      button.setAttribute("aria-expanded", expanded ? "true" : "false")
    })

    viewerSettingsAccordionPanels.forEach((panelElement) => {
      panelElement.hidden = state.viewerSettingsSection !== panelElement.dataset.viewerSettingsAccordionPanel
    })

    renderReferenceLayerMenu()
    renderReferenceLayerList()
    syncCablingPlanForm()
  }

  const setMappingTab = (tabName) => {
    state.mappingTab = tabName

    tabButtons.forEach((button) => {
      const active = button.dataset.mappingTab === tabName
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-selected", active ? "true" : "false")
    })

    tabPanels.forEach((panelElement) => {
      panelElement.hidden = panelElement.dataset.mappingTabPanel !== tabName
    })
  }

  const setPlacementTab = (tabName) => {
    state.placementTab = tabName

    placementTabButtons.forEach((button) => {
      const active = button.dataset.placementTab === tabName
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-selected", active ? "true" : "false")
    })

    placementTabPanels.forEach((panelElement) => {
      panelElement.hidden = panelElement.dataset.placementTabPanel !== tabName
    })
  }

  const setPanelMode = (mode) => {
    state.panelMode = !state.model && mode === "tag" ? "place" : mode

    panelModeButtons.forEach((button) => {
      const active = button.dataset.panelMode === state.panelMode
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-selected", active ? "true" : "false")
      button.setAttribute("aria-checked", active ? "true" : "false")
    })

    advancedPanelModeButtons.forEach((button) => {
      const active = button.dataset.advancedPanelMode === state.panelMode
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-pressed", active ? "true" : "false")
    })

    panelModePanels.forEach((panelElement) => {
      panelElement.hidden = panelElement.dataset.panelModePanel !== state.panelMode
    })
  }

  const selectedPlacementExists = () => Boolean(state.selectedPlacement)

  const guidedMaxStep = () => (["navigate", "capture"].includes(state.panelMode) ? 2 : 4)

  const guidedCanAdvance = () => {
    if (state.guidedStep <= 1) return true
    if (state.guidedStep >= guidedMaxStep()) return false

    if (["navigate", "capture"].includes(state.panelMode)) return false

    if (state.panelMode === "place") {
      if (state.guidedStep === 2) return Boolean(selectedPlacementPosition())
      if (state.guidedStep === 3) return selectedPlacementExists()
      return false
    }

    if (state.guidedStep === 2) return Boolean(selectedNodeKey())
    if (state.guidedStep === 3) return Boolean(selectedMapping())
    return false
  }

  const syncEditorMode = () => {
    state.editorMode = state.preferAdvancedEditor ? "advanced" : "guided"
    const tagModeAvailable = Boolean(state.model)

    const guidedMode = state.editorMode === "guided"
    const tagHasSelection = Boolean(selectedNodeKey())
    const placeHasPoint = Boolean(selectedPlacementPosition())
    const placeHasPlacement = selectedPlacementExists()

    guidedStepLabels.forEach((label) => {
      label.hidden = true
    })

    if (guidedEntry) guidedEntry.hidden = !guidedMode || state.guidedStep > 1
    if (guidedNav) guidedNav.hidden = !guidedMode
    if (guidedSharedStepLabel) {
      guidedSharedStepLabel.hidden = !guidedMode
      guidedSharedStepLabel.textContent = `Step ${state.guidedStep}`
    }
    if (guidedHeaderEl) guidedHeaderEl.hidden = guidedMode && state.guidedStep === 4
    if (advancedPanelModeSwitcher) advancedPanelModeSwitcher.hidden = guidedMode

    if (editorModeLink) {
      editorModeLink.textContent = guidedMode ? "Advanced mode" : "Guided mode"
    }

    panelModeButtons.forEach((button) => {
      const isTagMode = button.dataset.panelMode === "tag"
      button.disabled = isTagMode && !tagModeAvailable
      button.setAttribute("aria-disabled", button.disabled ? "true" : "false")
    })

    advancedPanelModeButtons.forEach((button) => {
      const isTagMode = button.dataset.advancedPanelMode === "tag"
      button.disabled = isTagMode && !tagModeAvailable
      button.setAttribute("aria-disabled", button.disabled ? "true" : "false")
    })

    if (guidedTitleEl) {
      if (!guidedMode) {
        guidedTitleEl.textContent = "Model Data"
      } else if (state.guidedStep === 1) {
        guidedTitleEl.textContent = "Markup Mode"
      } else {
        guidedTitleEl.textContent =
          state.panelMode === "place"
            ? "Place Object"
            : state.panelMode === "navigate"
              ? "Navigation Points"
              : state.panelMode === "capture"
                ? "Map Capture Session"
              : "Tag Existing"
      }
    }

    if (editorModeHintEl) {
      if (!guidedMode) {
        editorModeHintEl.textContent = "Advanced Mode is active for direct editing of the selected mapping, placement, or navigation point."
      } else if (state.guidedStep === 1) {
        editorModeHintEl.textContent = state.model
          ? "Choose whether you want to tag existing model geometry, place a new object, or add navigation points."
          : "Choose whether you want to place a new object or add navigation points."
      } else if (state.panelMode === "navigate" && state.guidedStep === 2) {
        editorModeHintEl.textContent = state.model
          ? "Click the model to drop navigation points. Select an existing point to remove it."
          : "Click the plan surface to drop navigation points. Select an existing point to remove it."
      } else if (state.panelMode === "capture" && state.guidedStep === 2) {
        editorModeHintEl.textContent = "Choose a capture session, adjust the 2D route overlay, then publish linked 360 photos."
      } else if (state.panelMode === "place" && state.guidedStep === 2) {
        editorModeHintEl.textContent = state.model
          ? "Choose a device type and click the model to pick a placement point."
          : "Choose a device type and click the plan surface to pick a placement point."
      } else if (state.panelMode === "place" && state.guidedStep === 3) {
        editorModeHintEl.textContent = "Create or attach the placed object."
      } else if (state.panelMode === "place" && state.guidedStep === 4) {
        editorModeHintEl.textContent = "Optionally link this placed object to an external integration."
      } else if (state.panelMode === "tag" && state.guidedStep === 2) {
        editorModeHintEl.textContent = "Click model geometry to choose the node you want to tag."
      } else if (state.panelMode === "tag" && state.guidedStep === 3) {
        editorModeHintEl.textContent = "Create or attach the thing for the selected node."
      } else {
        editorModeHintEl.textContent = "Optionally link this node to an external integration."
      }
    }

    if (tagSelectionSection) tagSelectionSection.hidden = guidedMode ? state.guidedStep !== 2 : false
    if (tagThingSection) tagThingSection.hidden = guidedMode ? state.guidedStep !== 3 : false
    if (tagIntegrationSection) tagIntegrationSection.hidden = guidedMode ? state.guidedStep !== 4 : false
    if (tagListSection) tagListSection.hidden = guidedMode

    if (placeSelectionSection) placeSelectionSection.hidden = guidedMode ? state.guidedStep !== 2 : false
    if (placeThingSection) placeThingSection.hidden = guidedMode ? state.guidedStep !== 3 : false
    if (placeIntegrationSection) placeIntegrationSection.hidden = guidedMode ? state.guidedStep !== 4 : false
    if (placeListSection) placeListSection.hidden = guidedMode
    if (navigateSelectionSection) navigateSelectionSection.hidden = guidedMode ? state.guidedStep !== 2 : false
    if (navigateListSection) navigateListSection.hidden = guidedMode ? state.guidedStep !== 2 : false

    const advancedTagAccordionVisible = !guidedMode && state.panelMode === "tag"
    advancedTagAccordionButtons.forEach((button) => {
      const expanded = advancedTagAccordionVisible && button.dataset.advancedTagAccordion === state.advancedTagSection
      button.hidden = !advancedTagAccordionVisible
      button.setAttribute("aria-expanded", expanded ? "true" : "false")
      button.classList.toggle("is-expanded", expanded)
    })
    advancedTagAccordionPanels.forEach((panelElement) => {
      if (guidedMode) {
        panelElement.hidden = false
        return
      }

      panelElement.hidden = !advancedTagAccordionVisible || panelElement.dataset.advancedTagAccordionPanel !== state.advancedTagSection
    })

    const advancedPlaceAccordionVisible = !guidedMode && state.panelMode === "place"
    advancedPlaceAccordionButtons.forEach((button) => {
      const expanded = advancedPlaceAccordionVisible && button.dataset.advancedPlaceAccordion === state.advancedPlaceSection
      button.hidden = !advancedPlaceAccordionVisible
      button.setAttribute("aria-expanded", expanded ? "true" : "false")
      button.classList.toggle("is-expanded", expanded)
    })
    advancedPlaceAccordionPanels.forEach((panelElement) => {
      if (guidedMode) {
        panelElement.hidden = false
        return
      }

      panelElement.hidden = !advancedPlaceAccordionVisible || panelElement.dataset.advancedPlaceAccordionPanel !== state.advancedPlaceSection
    })

    const advancedNavigateAccordionVisible = !guidedMode && state.panelMode === "navigate"
    advancedNavigateAccordionButtons.forEach((button) => {
      const expanded = advancedNavigateAccordionVisible && button.dataset.advancedNavigateAccordion === state.advancedNavigateSection
      button.hidden = !advancedNavigateAccordionVisible
      button.setAttribute("aria-expanded", expanded ? "true" : "false")
      button.classList.toggle("is-expanded", expanded)
    })
    advancedNavigateAccordionPanels.forEach((panelElement) => {
      if (guidedMode) {
        panelElement.hidden = false
        return
      }

      panelElement.hidden = !advancedNavigateAccordionVisible || panelElement.dataset.advancedNavigateAccordionPanel !== state.advancedNavigateSection
    })

    const advancedCaptureAccordionVisible = !guidedMode && state.panelMode === "capture"
    advancedCaptureAccordionButtons.forEach((button) => {
      const expanded = advancedCaptureAccordionVisible && button.dataset.advancedCaptureAccordion === state.advancedCaptureSection
      button.hidden = !advancedCaptureAccordionVisible
      button.setAttribute("aria-expanded", expanded ? "true" : "false")
      button.classList.toggle("is-expanded", expanded)
    })
    advancedCaptureAccordionPanels.forEach((panelElement) => {
      if (guidedMode) {
        panelElement.hidden = false
        return
      }

      panelElement.hidden = !advancedCaptureAccordionVisible || panelElement.dataset.advancedCaptureAccordionPanel !== state.advancedCaptureSection
    })

    if (guidedPrevButton) {
      guidedPrevButton.disabled = state.guidedStep <= 1
    }

    if (guidedStepNextButton) {
      const onLastStep = state.guidedStep >= guidedMaxStep()
      guidedStepNextButton.hidden = !guidedMode || onLastStep
      guidedStepNextButton.disabled = !guidedCanAdvance()
      guidedStepNextButton.textContent = "Next"
    }

    if (guidedFinishButton) {
      guidedFinishButton.hidden = !guidedMode || state.guidedStep < guidedMaxStep()
    }

    return {
      tagHasSelection,
      placeHasPoint,
      placeHasPlacement
    }
  }

  const closeMappingPanel = () => {
    state.sidePanelMode = null
    state.mappingMode = false
    state.guidedStep = 1
    state.preferAdvancedEditor = false
    state.advancedNavigateSection = null
    state.advancedCaptureSection = "sessions"
    clearHoveredObject()
    clearSelectedObject()
    renderer.domElement.style.cursor = "default"
    renderInspector()
    setFeedback("Mapping mode is off.", "neutral")
  }

  return {
    renderCablingPlanEditor,
    setMappingTab,
    setPlacementTab,
    setPanelMode,
    guidedMaxStep,
    guidedCanAdvance,
    syncEditorMode,
    closeMappingPanel
  }
}
